import { Page, SectionBlock } from "../components/Page";

export function Voice() {
  return (
    <Page
      number="02"
      title="Voice & language"
      eyebrow="How we sound"
      intro={
        <>
          Our copy carries more brand than our logo does. People read more of us
          than they ever look at us. Voice deserves the most patient attention
          on this page.
        </>
      }
    >
      <SectionBlock title="The voice in one paragraph">
        <p className="font-display text-[26px] leading-[1.3] italic text-ink">
          We sound like a friend who sits, not a guru who instructs.
          Plain-spoken, slightly understated, lightly affectionate. We don't
          oversell, we don't underplay, we don't use jargon, and we never shout.
          When in doubt, write the sentence the way you'd say it to someone over
          tea — and then take out the extra word.
        </p>
      </SectionBlock>

      <SectionBlock title="Principles">
        <div className="grid gap-5 md:grid-cols-2">
          {[
            {
              t: "Plain language wins",
              body: "Short words, short sentences, real verbs. If a word makes you sound smarter, cut it. If a sentence works without an adverb, cut it.",
            },
            {
              t: "Specific over abstract",
              body: "‘Ten minutes morning, ten minutes evening’ beats ‘a daily contemplative practice.’ Numbers ground us. Times ground us. Places ground us.",
            },
            {
              t: "Warm, not chirpy",
              body: "Warmth comes from the rhythm of the sentence and the choice of verb. Not from exclamation marks. Never from emoji walls.",
            },
            {
              t: "Honest about what it is",
              body: "We don't promise transformation. We describe the practice and what people typically notice. The reader makes their own meaning.",
            },
            {
              t: "Invite, don't recruit",
              body: "Every CTA is a door, not a funnel. ‘Come sit with us’ is the spirit. ‘Sign up now’ is not.",
            },
            {
              t: "Leave room",
              body: "Allow silence. Allow short paragraphs. The reader needs space the same way the practice does.",
            },
          ].map((p) => (
            <div
              key={p.t}
              className="border-l-2 pl-5"
              style={{ borderColor: "var(--amber)" }}
            >
              <p className="font-display text-[22px] text-ink">{p.t}</p>
              <p className="mt-2 font-body text-[14.5px] leading-relaxed text-ink-soft">
                {p.body}
              </p>
            </div>
          ))}
        </div>
      </SectionBlock>

      <SectionBlock title="Vocabulary — words we use">
        <div className="grid gap-5 md:grid-cols-2">
          <div>
            <p className="eyebrow text-amber">Words we use</p>
            <ul className="mt-4 grid grid-cols-2 gap-y-2 font-body text-[15px] text-ink">
              {[
                "sit",
                "practice",
                "meditation",
                "the technique",
                "come along",
                "carefully",
                "quietly",
                "ten minutes",
                "morning and evening",
                "Sunday",
                "ancient",
                "lineage",
                "Sydney",
                "in person · online",
              ].map((w) => (
                <li key={w} className="font-display italic">
                  {w}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="eyebrow text-ink-soft">Words we avoid</p>
            <ul className="mt-4 grid grid-cols-2 gap-y-2 font-body text-[15px] text-ink-soft line-through decoration-[1px]">
              {[
                "wellness",
                "mindfulness™",
                "biohack",
                "self-care",
                "unlock",
                "transform your life",
                "guru",
                "sacred",
                "divine",
                "exclusive",
                "limited spots",
                "sign up now",
                "powerful",
                "the secret to",
              ].map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          </div>
        </div>
      </SectionBlock>

      <SectionBlock title="Examples — same idea, three takes">
        <div className="grid gap-6 md:grid-cols-3">
          {[
            {
              tone: "Too dressed",
              copy: "Unlock the ancient secret to transformational inner peace — discover what gurus have known for centuries.",
              note: "Salesy. Promises a result. Borrows mystique.",
              good: false,
            },
            {
              tone: "Too plain",
              copy: "Meditation class. Sunday 10am. Free.",
              note: "Accurate but cold. Reads like a noticeboard.",
              good: false,
            },
            {
              tone: "Just right",
              copy: "A simple meditation, taught carefully — ten minutes morning, ten minutes evening. Come sit with us this Sunday.",
              note: "Plain, specific, warm. Tells you what it is, when, and how to join.",
              good: true,
            },
          ].map((ex) => (
            <div
              key={ex.tone}
              className="surface flex flex-col gap-4 p-5"
              style={{
                background: "var(--paper)",
                outline: ex.good ? "1.5px solid var(--amber)" : "none",
              }}
            >
              <p
                className="eyebrow"
                style={{ color: ex.good ? "var(--amber)" : "var(--ink-soft)" }}
              >
                {ex.tone}
              </p>
              <p className="font-display text-[19px] leading-snug text-ink">
                "{ex.copy}"
              </p>
              <p className="font-body text-[13px] text-ink-soft">{ex.note}</p>
            </div>
          ))}
        </div>
      </SectionBlock>

      <SectionBlock title="Voice in context">
        <div className="grid gap-5 md:grid-cols-2">
          {[
            {
              context: "Website hero",
              line: "Ten minutes in the morning. Ten in the evening.",
            },
            {
              context: "Meetup invite",
              line: "Come sit with us this Sunday — Sydney CBD, 10am, free.",
            },
            {
              context: "Confirmation email",
              line: "Thanks for joining. We'll see you at the session.",
            },
            {
              context: "Challenge nudge",
              line: "Day 4 of 11 — ten minutes is enough today.",
            },
            {
              context: "Refusal",
              line: "Not today. There's another sitting on Sunday.",
            },
            {
              context: "Brand footer",
              line: "Vihangam Yog Australia — unwinding spirituality.",
            },
          ].map((ex) => (
            <div
              key={ex.context}
              className="flex flex-col gap-1.5 border-t border-rule pt-4 first:border-t-0 first:pt-0"
            >
              <p className="eyebrow">{ex.context}</p>
              <p className="font-display text-[20px] italic text-ink">
                "{ex.line}"
              </p>
            </div>
          ))}
        </div>
      </SectionBlock>
    </Page>
  );
}
